
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardHeader, 
  CardTitle 
} from '@/components/ui/card';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger 
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { MoreHorizontal, FileDown, Search, ChevronDown, BarChart3, Loader2 } from 'lucide-react';
import { Avatar } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { useResults } from '@/hooks/useResults';
import { Alert, AlertDescription } from '@/components/ui/alert';

export default function Results() {
  const {
    results,
    isLoading,
    error,
    searchTerm,
    setSearchTerm,
    selectedAssessment,
    setSelectedAssessment,
    assessmentNames,
    exportToCSV
  } = useResults();

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertDescription>
          Failed to load results: {error instanceof Error ? error.message : 'Unknown error'}
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-end">
        <Button 
          variant="outline" 
          size="sm"
          onClick={exportToCSV}
          disabled={results.length === 0}
        >
          <FileDown className="mr-2 h-4 w-4" />
          Export
        </Button>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <CardTitle>Assessment Results</CardTitle>
              <CardDescription>
                Recent submissions from all assessments
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              {/* Search Input */}
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search respondents..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-8 w-64"
                />
              </div>
              
              {/* Assessment Filter */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm">
                    {selectedAssessment} <ChevronDown className="ml-2 h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {assessmentNames.map((name) => (
                    <DropdownMenuItem 
                      key={name}
                      onClick={() => setSelectedAssessment(name)}
                    >
                      {name}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-10">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground mb-2" />
              <p className="text-muted-foreground">Loading results...</p>
            </div>
          ) : results.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Respondent</TableHead>
                  <TableHead>Assessment</TableHead>
                  <TableHead>Company</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Score</TableHead>
                  <TableHead className="w-[80px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {results.map((result) => (
                  <TableRow key={result.id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <Avatar>
                          <div className="flex h-full w-full items-center justify-center bg-muted text-xs font-medium uppercase text-muted-foreground">
                            {result.respondentName.substring(0, 2)}
                          </div>
                        </Avatar>
                        <div className="flex flex-col">
                          <span className="font-medium">{result.respondentName}</span>
                          <span className="text-xs text-muted-foreground">{result.respondentEmail}</span>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>{result.assessmentName}</TableCell>
                    <TableCell>{result.company || 'N/A'}</TableCell>
                    <TableCell>
                      {new Date(result.submittedAt).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <Badge className={`${
                        result.averageScore >= 3.5 ? "bg-green-100 text-green-800" :
                        result.averageScore >= 2.5 ? "bg-yellow-100 text-yellow-800" :
                        "bg-red-100 text-red-800"
                      }`}>
                        {result.averageScore}/4
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreHorizontal className="h-4 w-4" />
                            <span className="sr-only">Open menu</span>
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem>View details</DropdownMenuItem>
                          <DropdownMenuItem>Download report</DropdownMenuItem>
                          <DropdownMenuItem>Send email</DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="flex flex-col items-center justify-center py-10">
              <BarChart3 className="h-12 w-12 text-muted-foreground mb-2" />
              <h3 className="text-lg font-medium">
                {searchTerm || selectedAssessment !== 'All Assessments' 
                  ? 'No matching results' 
                  : 'No results yet'
                }
              </h3>
              <p className="text-center text-muted-foreground mt-1">
                {searchTerm || selectedAssessment !== 'All Assessments'
                  ? 'Try adjusting your search or filter criteria.'
                  : 'When users complete assessments, their results will appear here.'
                }
              </p>
              {(searchTerm || selectedAssessment !== 'All Assessments') && (
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="mt-4"
                  onClick={() => {
                    setSearchTerm('');
                    setSelectedAssessment('All Assessments');
                  }}
                >
                  Clear filters
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
